/**
 * diffStore — Zustand store keyed by ``doc_id`` carrying the most recent
 * ``doc_diff_payload`` WS envelope per document.
 *
 * Pipeline (Phase 2.1 May 2026):
 *   1. ``WSClient`` parses a ``doc_diff_payload`` frame (zod schema in
 *      ``@ando/core/ws/schemas``).
 *   2. ``useDocDiffPayloadBridge`` calls ``setDiffPayload(payload)``.
 *   3. The mounted ``<ArtifactPane>`` (or a parent surface) reads its own
 *      ``doc_id`` slice via ``useDiffPayload(docId)``, builds a mime-specific
 *      ``DiffSource`` adapter, and feeds it to ``<ReviewRail>``.
 *
 * Why a separate store from ``proposalsStore``:
 *   - ``proposalsStore`` carries the PER-CHANGE preview-apply queue (one
 *     ``doc_edit_proposal`` per cell/range). It survives across reconnects
 *     because the BE replays history.
 *   - ``diffStore`` carries the AGGREGATE diff envelope — a single
 *     ``doc_diff_payload`` summarising the whole proposal as a list of
 *     atomic changes for the review rail. One envelope replaces the
 *     previous one (latest BE summary wins) — no replay dedup needed
 *     because the envelope itself is idempotent by ``proposal_id``.
 *
 * Persistence: in-memory only. The BE re-emits on reconnect through the
 * standard chat history replay path.
 *
 * Decision discipline: ``decisionByChange`` tracks the user's local
 * accept/reject per ``change_id``. Pure FE state; the upstream commit
 * happens via the existing ``execute_action`` flow when the user clicks
 * Confirm in the ActionDrawer (or via a future per-change BE endpoint).
 */

import { create } from "zustand";
import { useShallow } from "zustand/shallow";

import type { DocDiffPayload } from "@ando/core";

export type DiffDecision = "accepted" | "rejected" | null;

interface DiffState {
  /** Latest envelope per doc — replaces previous on new emit. */
  readonly byDoc: Readonly<Record<string, DocDiffPayload>>;
  /** Per-change local decision keyed by ``change_id``. */
  readonly decisionByChange: Readonly<Record<string, DiffDecision>>;
  setDiffPayload: (payload: DocDiffPayload) => void;
  setDecision: (changeId: string, decision: DiffDecision) => void;
  /** Bulk-set every change in a doc to the same decision. */
  setAllDecisions: (docId: string, decision: DiffDecision) => void;
  clearForDoc: (docId: string) => void;
  clearForProposal: (proposalId: string) => void;
}

export const useDiffStore = create<DiffState>((set) => ({
  byDoc: {},
  decisionByChange: {},
  setDiffPayload: (payload) => {
    set((prev) => {
      // Idempotent on identical reference — guard against accidental
      // re-render storms when the bridge forwards a memoised event.
      if (prev.byDoc[payload.doc_id] === payload) return prev;
      return {
        byDoc: {
          ...prev.byDoc,
          [payload.doc_id]: payload,
        },
      };
    });
  },
  setDecision: (changeId, decision) => {
    set((prev) => {
      if (prev.decisionByChange[changeId] === decision) return prev;
      // ``decision === null`` rolls back to pending — drop the key
      // instead of carrying a null so memory stays bounded.
      const next = { ...prev.decisionByChange };
      if (decision === null) {
        delete next[changeId];
      } else {
        next[changeId] = decision;
      }
      return { decisionByChange: next };
    });
  },
  setAllDecisions: (docId, decision) => {
    set((prev) => {
      const payload = prev.byDoc[docId];
      if (!payload) return prev;
      const next = { ...prev.decisionByChange };
      for (const c of payload.changes) {
        if (decision === null) {
          delete next[c.change_id];
        } else {
          next[c.change_id] = decision;
        }
      }
      return { decisionByChange: next };
    });
  },
  clearForProposal: (proposalId) => {
    set((prev) => {
      const envelopes = Object.values(prev.byDoc).filter((p) => p.proposal_id === proposalId);
      if (!envelopes.length) return prev;
      const byDoc = { ...prev.byDoc };
      const decisionByChange = { ...prev.decisionByChange };
      for (const envelope of envelopes) {
        delete byDoc[envelope.doc_id];
        for (const change of envelope.changes) delete decisionByChange[change.change_id];
      }
      return { byDoc, decisionByChange };
    });
  },
  clearForDoc: (docId) => {
    set((prev) => {
      const envelope = prev.byDoc[docId];
      if (!envelope) return prev;
      const { [docId]: _, ...restByDoc } = prev.byDoc;
      void _;
      // Also drop per-change decisions tied to this doc to keep the
      // map from growing without bound across long sessions.
      const restDecisions = { ...prev.decisionByChange };
      for (const c of envelope.changes) delete restDecisions[c.change_id];
      return { byDoc: restByDoc, decisionByChange: restDecisions };
    });
  },
}));

/**
 * Selector — referentially-stable envelope for a single ``docId``. Returns
 * ``null`` until the BE emits the first ``doc_diff_payload``.
 *
 * Stable on identity until ``setDiffPayload`` writes a NEW envelope for
 * the same doc — React 19 / Zustand 5's ``useSyncExternalStore`` snapshot
 * comparison won't trip an infinite-loop because we don't derive a new
 * array reference on every snapshot.
 */
export function selectDiffPayload(
  docId: string,
): (s: DiffState) => DocDiffPayload | null {
  return (s) => s.byDoc[docId] ?? null;
}

/**
 * Hook — convenience wrapper returning the per-doc envelope. Pass through
 * ``useShallow`` when the consumer derives an array off the envelope (e.g.
 * filtered changes); the envelope reference itself is already stable.
 */
export function useDiffPayload(docId: string): DocDiffPayload | null {
  return useDiffStore(selectDiffPayload(docId));
}

/**
 * Selector — resilient envelope lookup: by ``docId`` first, else by
 * ``proposalId``.
 *
 * The SAME document can be addressed by more than one id form
 * (``weaviate_doc_id`` ``fork_…`` vs the ``content_items.id`` UUID). If the
 * redline envelope is keyed by a DIFFERENT form than the open editor's
 * ``docId``, a plain ``byDoc[docId]`` miss leaves the review rail silently
 * empty (June 18, 2026 incident). The ``proposalId`` carried by the active
 * action is form-independent, so we fall back to it. The return value is an
 * existing ``byDoc`` reference (or ``null``) — never a freshly-built object —
 * so ``useSyncExternalStore`` stays infinite-loop-safe.
 */
export function selectDiffPayloadResolved(
  docId: string,
  proposalId?: string | null,
): (s: DiffState) => DocDiffPayload | null {
  return (s) => {
    const byId = s.byDoc[docId];
    if (byId && (!proposalId || byId.proposal_id === proposalId)) return byId;
    if (proposalId) {
      for (const p of Object.values(s.byDoc)) {
        if (p.proposal_id === proposalId) return p;
      }
    }
    return null;
  };
}

/**
 * Hook — resilient per-doc envelope (``docId`` OR ``proposalId`` of the active
 * action). Prefer this over :func:`useDiffPayload` wherever the consumer knows
 * the proposal id, so an id-form mismatch can never blank the rail.
 */
export function useDiffPayloadResolved(
  docId: string,
  proposalId?: string | null,
): DocDiffPayload | null {
  return useDiffStore(selectDiffPayloadResolved(docId, proposalId));
}

/**
 * Hook — boolean "is there an active diff envelope for this doc?".
 *
 * Used by the focused-action surface to SUPPRESS the separate
 * ``action_proposal`` overlay for document-class edits whose review is
 * already happening through the in-editor redline rail (the
 * ``doc_diff_payload`` envelope). One review surface — the in-slide rail —
 * not a redundant proposal card. Returns a primitive so the
 * ``useSyncExternalStore`` snapshot never trips an infinite loop.
 *
 * ``docId`` may be ``undefined`` (non-document proposals carry no doc id);
 * we short-circuit to ``false`` so those proposals keep their overlay.
 *
 * ``proposalId`` is an OPTIONAL fallback: the SAME document is addressable by
 * more than one id form (the diff envelope may be keyed by the fork's
 * ``weaviate_doc_id`` ``fork_…`` while the action carries a different form). A
 * plain ``byDoc[docId]`` miss would then silently fail to suppress, re-creating
 * the double-surface bug (June 18, 2026). Mirrors
 * :func:`selectDiffPayloadResolved`: try ``docId`` first, else scan for an
 * envelope whose ``proposal_id`` matches. Still returns a primitive.
 */
export function useHasDiffForDoc(
  docId: string | undefined,
  proposalId?: string | null,
): boolean {
  return useDiffStore((s) => {
    if (docId && s.byDoc[docId] && (!proposalId || s.byDoc[docId]?.proposal_id === proposalId)) return true;
    if (proposalId) {
      for (const p of Object.values(s.byDoc)) {
        if (p.proposal_id === proposalId) return true;
      }
    }
    return false;
  });
}

/**
 * Hook — current per-change decisions map (only the entries with a
 * non-null decision). Returns a shallow-stable shape (only re-renders
 * when keys/values actually mutate) to keep the rail's per-card
 * accept/reject state cheap to derive.
 */
export function useDecisionsByChange(): Readonly<Record<string, DiffDecision>> {
  return useDiffStore(useShallow((s) => s.decisionByChange));
}
